import { Controller } from "@hotwired/stimulus"

// Tax Location on the final step of the admin enrolment wizard.
// Shows the office list or the address fields depending on the chosen type.
export default class extends Controller {
  static targets = ["officeSection", "addressSection", "outsideWaWarning"];

  // Every zip the resolution chain could place the student by. config/wa_tax_rates.yml
  // holds WA zips only, so if none of these is a WA zip nothing in the chain can match and
  // the rate falls through to the school's own office.
  static ZIP_FIELD_IDS = [
    "zipcode_value", // the student's own address, step 1
    "booking_location_pickup_home_work_zipcode_value", // the pick-up, when typed
  ];

  static WA_ZIP = /^9[89]\d{3}/;

  // Step 1 of the same form. The wizard is one form with client-side steps, so the pick-up
  // the admin typed is already in the DOM by the time this step is shown.
  //
  // Keyed by element ID, not name. Both steps render the address widget through a Rails
  // form builder, which namespaces the submitted names to driving_school_booking[...] —
  // so matching on name found nothing. The ids are passed explicitly and stay flat.
  static PREFILL_PAIRS = [
    ["booking_location_pickup_home_work_address_value", "enrolment_tax_address_value"],
    ["booking_location_pickup_home_work_address_line_2_value", "enrolment_tax_address_line_2_value"],
    ["booking_location_pickup_home_work_city_value", "enrolment_tax_city_value"],
    ["booking_location_pickup_home_work_state_value", "enrolment_tax_state_value"],
    ["booking_location_pickup_home_work_zipcode_value", "enrolment_tax_postcode_value"],
    ["booking_location_pickup_home_work_country_value", "enrolment_tax_country_value"],
  ];

  connect() {
    this.toggle();

    // The zips are filled in on earlier steps, and the admin can go back and change them,
    // so re-check on every edit rather than only when this step first renders.
    this.checkOutsideWa = this.checkOutsideWa.bind(this);
    this.constructor.ZIP_FIELD_IDS.forEach((id) => {
      const field = document.getElementById(id);
      if (field) field.addEventListener("input", this.checkOutsideWa);
    });

    this.checkOutsideWa();
  }

  disconnect() {
    this.constructor.ZIP_FIELD_IDS.forEach((id) => {
      const field = document.getElementById(id);
      if (field) field.removeEventListener("input", this.checkOutsideWa);
    });
  }

  // Warn only when we have something to judge AND none of it is in WA. An empty form is
  // not evidence of anything, and a pick-up POINT leaves the typed zip blank while still
  // being a perfectly good WA location — warning on either would train admins to ignore it.
  checkOutsideWa() {
    if (!this.hasOutsideWaWarningTarget) return;

    const entered = this.constructor.ZIP_FIELD_IDS.map((id) => {
      const field = document.getElementById(id);
      return field ? field.value.trim() : "";
    }).filter((value) => value !== "");

    const outside =
      entered.length > 0 &&
      !entered.some((zip) => this.constructor.WA_ZIP.test(zip));

    this.outsideWaWarningTarget.hidden = !outside;
  }

  toggle() {
    const selected = this.element.querySelector(
      "input[name='enrolment_tax_location_type']:checked"
    );
    const value = selected ? selected.value : null;

    if (this.hasOfficeSectionTarget) {
      this.officeSectionTarget.hidden = value !== "office";
    }

    if (this.hasAddressSectionTarget) {
      this.addressSectionTarget.hidden = value !== "address";
      if (value === "address") this.prefillFromPickUp();
    }
  }

  // Convenience only, and only into blanks — never overwrites something the admin has
  // already typed, including on a second visit to this step.
  //
  // Deliberately does NOT pre-select the "address" radio. Leaving the whole section unset
  // is the neutral state, and that matters: a tax location here is saved to the STUDENT and
  // outranks the school's drive-location sourcing for every later booking. Auto-filling a
  // choice would quietly freeze this student to today's pick-up address.
  prefillFromPickUp() {
    this.constructor.PREFILL_PAIRS.forEach(([sourceId, targetId]) => {
      const source = document.getElementById(sourceId);
      const target = document.getElementById(targetId);
      if (!source || !target) return;
      if (target.value.trim() !== "") return;

      const value = source.value.trim();
      if (value !== "") target.value = value;
    });
  }

  // Back to the neutral state: no type submitted, so the controller applies no tax
  // location and no exemption, and the normal resolution chain governs. Also drops any
  // office already picked, or it would submit alongside a blank type.
  clear(event) {
    event.preventDefault();

    this.element
      .querySelectorAll(
        "input[name='enrolment_tax_location_type'], input[name='enrolment_tax_office_id']"
      )
      .forEach((input) => {
        input.checked = false;
      });

    this.toggle();
  }
}
